import { useCallback } from "react";
import { calculatePremium } from "../../../premium/domain/calculatePremium";
import { applyPremiumRecalcToPayload } from "../bondWizardStepHelpers";
import { buildBondFormCreatePayload } from "../bondPayloadBuilders";
import { validateBondFormStep } from "../bondFormValidationHelpers";
import {
  runDuplicateBondCheck,
  finalizeBondCreation,
} from "../bondFormCreationHelpers";

/**
 * @param {import("../bondFormConfig").BondFormKindConfig} config
 * @param {import("./useBondFormBase").BondFormBaseContext} ctx
 */
export function useStandardBondKindLogic(config, ctx) {
  const { isGuestRequest, globalState, setIsLoading } = ctx;

  const validateStepData = useCallback(
    (data, step, state) =>
      validateBondFormStep(config, data, step, state, isGuestRequest),
    [isGuestRequest]
  );

  const generatePayload = useCallback(
    (state) =>
      buildBondFormCreatePayload(config, state, {
        isGuestRequest,
        userdetails: ctx.userdetails,
        lawfirmdetails: ctx.lawfirmdetails,
      }),
    [isGuestRequest, ctx.userdetails, ctx.lawfirmdetails]
  );

  const createBond = useCallback(
    async (payload, options = {}) => {
      await finalizeBondCreation(ctx, payload, options);
    },
    [ctx]
  );

  const handleFinish = useCallback(async () => {
    setIsLoading(true);
    const payload = generatePayload(globalState);
    applyPremiumRecalcToPayload(
      payload,
      calculatePremium,
      config.getPremiumRecalcInputs(globalState)
    );
    const isDuplicate = await runDuplicateBondCheck(ctx, payload);
    if (!isDuplicate) {
      await createBond(payload);
    }
  }, [ctx, globalState, generatePayload, createBond, setIsLoading]);

  return { validateStepData, handleFinish, createBond, generatePayload };
}

/** @param {import("../bondFormConfig").BondFormKindConfig} config */
export function createBondKindLogic(config) {
  return function useBondKindLogic(ctx) {
    return useStandardBondKindLogic(config, ctx);
  };
}
